/**
 * Keyboard Shortcuts Dialog
 *
 * Displays available keyboard shortcuts for the application.
 * Accessible via ? key or Help menu.
 *
 * WCAG 2.1.1 Keyboard: All functionality available via keyboard
 */

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Keyboard } from 'lucide-react';

interface ShortcutGroup {
  title: string;
  shortcuts: Array<{
    keys: string[];
    description: string;
  }>;
}

const SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['Ctrl/⌘', 'K'], description: 'Open command palette' },
      { keys: ['Tab'], description: 'Move to next interactive element' },
      { keys: ['Shift', 'Tab'], description: 'Move to previous interactive element' },
      { keys: ['Enter'], description: 'Activate button or link' },
      { keys: ['Space'], description: 'Activate button or toggle checkbox' },
      { keys: ['Escape'], description: 'Close dialog or cancel action' },
    ],
  },
  {
    title: 'Accessibility',
    shortcuts: [
      { keys: ['Alt', '0'], description: 'Skip to main content (on page load)' },
      { keys: ['?'], description: 'Show keyboard shortcuts (this dialog)' },
    ],
  },
  {
    title: 'Sidebar',
    shortcuts: [{ keys: ['Ctrl/⌘', 'B'], description: 'Toggle sidebar (expanded/minimal/hidden)' }],
  },
  {
    title: 'Tables',
    shortcuts: [
      { keys: ['↑', '↓'], description: 'Navigate table rows (when focused)' },
      { keys: ['Enter'], description: 'Open/edit item in table' },
    ],
  },
];

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsDialog({ open, onClose }: KeyboardShortcutsDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50" />
        <Dialog.Content
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] bg-white dark:bg-zinc-800 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg"
          aria-describedby="keyboard-shortcuts-description"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-3">
              <Keyboard className="h-6 w-6 text-sky-600 dark:text-sky-400" />
              <Dialog.Title className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Keyboard Shortcuts
              </Dialog.Title>
            </div>
            <Dialog.Close className="rounded-xs opacity-70 hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-sky-400">
              <X size={20} className="text-zinc-500 dark:text-zinc-400" />
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <p
              id="keyboard-shortcuts-description"
              className="text-sm text-zinc-600 dark:text-zinc-400 mb-6"
            >
              Use these keyboard shortcuts to navigate the application efficiently. All
              functionality is accessible via keyboard for screen reader and keyboard-only users.
            </p>

            <div className="space-y-6">
              {SHORTCUTS.map((group) => (
                <div key={group.title}>
                  <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
                    {group.title}
                  </h3>
                  <div className="space-y-2">
                    {group.shortcuts.map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
                      >
                        <span className="text-sm text-zinc-700 dark:text-zinc-300">
                          {shortcut.description}
                        </span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, keyIndex) => (
                            <React.Fragment key={keyIndex}>
                              {keyIndex > 0 && (
                                <span className="text-zinc-400 dark:text-zinc-600 mx-1">+</span>
                              )}
                              <kbd className="px-2 py-1 text-xs font-semibold text-zinc-800 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-700 border border-zinc-300 dark:border-zinc-600 rounded-sm">
                                {key}
                              </kbd>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-zinc-200 dark:border-zinc-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
            >
              Close
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
